import { parseDateOnly } from '@core/utils/formatDateOnly';

type PaginatedLike<T> = {
  data?: T[];
  total?: number;
  meta?: {
    total?: number;
    data?: T[];
  };
  items?: T[];
};

type TagLike = {
  until?: string;
  report_tag_status_name?: string;
};

export function countTagsByStatus<T extends TagLike>(
  tags: T[],
  statuses: string | string[]
): number {
  const allowedStatuses = new Set(
    (Array.isArray(statuses) ? statuses : [statuses]).map((status) => status.toUpperCase())
  );

  return tags.filter((tag) => {
    const status = String(tag.report_tag_status_name ?? '').toUpperCase();
    return allowedStatuses.has(status);
  }).length;
}

export function getRows<T>(payload: unknown): T[] {
  const data = payload as PaginatedLike<T> | T[] | null | undefined;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  const metaData = data.meta?.data;
  if (Array.isArray(metaData)) return metaData;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

export function getTotalCount(payload: unknown): number {
  const data = payload as PaginatedLike<unknown> | null | undefined;
  const candidates = [data?.total, data?.meta?.total];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return getRows(payload).length;
}

export function getUpcomingDeadlines<T extends TagLike>(tags: T[], limit = 5): T[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return tags
    .filter((tag) => {
      const status = String(tag.report_tag_status_name ?? '').toUpperCase();
      if (!(status === 'OPEN' || status === 'PENDING')) return false;
      const untilDate = parseDateOnly(tag.until);
      return !!untilDate && untilDate.getTime() >= today.getTime();
    })
    .sort((a, b) => {
      const aTime = parseDateOnly(a.until)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bTime = parseDateOnly(b.until)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    })
    .slice(0, limit);
}

type DeadlineAlertLike = {
  type: string;
  days_until_deadline?: number | null;
};

const DEADLINE_PASSED_TYPE = 'DEADLINE_PASSED';
const DEADLINE_URGENT_TYPE = 'DEADLINE_URGENT';

export function isDeadlineAlertType(type: string): boolean {
  return type === DEADLINE_PASSED_TYPE || type === DEADLINE_URGENT_TYPE;
}

/** Overdue first (most days late first), then due soon (soonest first). */
export function sortDeadlineAlerts<T extends DeadlineAlertLike>(alerts: T[]): T[] {
  const rank = (alert: T) => (alert.type === DEADLINE_PASSED_TYPE ? 0 : 1);
  return alerts
    .filter((alert) => isDeadlineAlertType(alert.type))
    .slice()
    .sort((a, b) => {
      const byType = rank(a) - rank(b);
      if (byType !== 0) return byType;
      const aDays = a.days_until_deadline ?? Number.MAX_SAFE_INTEGER;
      const bDays = b.days_until_deadline ?? Number.MAX_SAFE_INTEGER;
      return aDays - bDays;
    });
}

type ChecklistPeriodLike = {
  report_tag_id: number;
  days_until_deadline?: number | null;
  templates?: { outstanding?: number };
  department_reports?: { outstanding?: number };
};

/** Period with outstanding work and the smallest days until deadline; else the first period. */
export function mostUrgentChecklistPeriodId<T extends ChecklistPeriodLike>(
  periods: T[]
): number | null {
  if (!periods.length) return null;
  let best: T | null = null;
  for (const period of periods) {
    const outstanding =
      (period.templates?.outstanding ?? 0) + (period.department_reports?.outstanding ?? 0);
    if (outstanding <= 0 || period.days_until_deadline == null) continue;
    if (best === null || period.days_until_deadline < (best.days_until_deadline as number)) {
      best = period;
    }
  }
  if (best) return best.report_tag_id;
  const firstOutstanding = periods.find(
    (period) =>
      (period.templates?.outstanding ?? 0) + (period.department_reports?.outstanding ?? 0) > 0
  );
  return (firstOutstanding ?? periods[0]).report_tag_id;
}
